// src/components/wallet/ULTRARewards.js
import React, { useEffect, useState, useCallback } from 'react';
import { formatCurrencyExtended } from '../../utils';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Progress } from '@nextui-org/react';

const ULTRARewards = React.memo(
  ({ token, account, contracts, viewTotalDistributed = false, enableClaiming = false }) => {
    const [unclaimedRewards, setUnclaimedRewards]     = useState(0);
    const [totalDistributed, setTotalDistributed]     = useState(0);
    const [totalRealised,   setTotalRealised]         = useState(0);
    const [isClaiming,      setIsClaiming]            = useState(false);

    /* ─────────────────────────────────────────────────────────
       centralised fetch so we can reuse it after a claim/poll ─ */
    const fetchRewards = useCallback(async () => {
      if (!contracts?.ultraDistributor || !account || !token?.address) return;

      const [
        reflectionPool,
        unrealisedPlusRealisedUndeposited,
        shareInfo
      ] = await Promise.all([
        contracts.ultraDistributor.methods
          .reflectionPoolAtERC20(token.address)
          .call(),

        contracts.ultraDistributor.methods
          .getTotalUnrealisedWithRealisedUndeposited(account, token.address)
          .call(),

        contracts.ultraDistributor.methods
          .shares(account, token.address)
          .call()
      ]);

      setUnclaimedRewards(unrealisedPlusRealisedUndeposited);
      setTotalDistributed(reflectionPool?.data?.totalDistributed ?? 0);
      setTotalRealised(shareInfo.totalRealised);
    }, [contracts?.ultraDistributor, account, token?.address]);

    /* initial + polling refresh (60 s) */
    useEffect(() => {
      fetchRewards();                       // initial
      const id = setInterval(fetchRewards, 60_000);
      return () => clearInterval(id);
    }, [fetchRewards]);

    /* handle claim – no parent callback needed */
    const handleClaim = async () => {
      if (!enableClaiming || isClaiming) return;
      setIsClaiming(true);

      try {
        await contracts.ultraDistributor.methods
              .claimDividends(token.address)
              .send({ from: account });

        await fetchRewards();               // self-refresh
      } catch (err) {
        console.error('claim error', err);
      } finally {
        setIsClaiming(false);
      }
    };

    const tokenDecimals = 10 ** token.decimals;
    const fmt = v => formatCurrencyExtended((v / tokenDecimals) * token.price);

    return (
      <div style={{
        borderRadius: 10,
        background   : 'rgba(50,100,255,0.05)',
        padding      : 5,
        fontSize     : 11
      }}>
        <div>Reward: <strong>{token.label}</strong></div>

        {viewTotalDistributed && (
          <div>
            Total&nbsp;Distributed:&nbsp;
            <strong>
              {totalDistributed
                ? fmt(totalDistributed)
                : <Skeleton width="100%" height="12px" className="mb-2" />
              }
            </strong>
          </div>
        )}

        {+totalRealised === 0 && +unclaimedRewards === 0 && (
          <div><strong>No rewards pending</strong></div>
        )}

        {totalRealised > 0 && (
          <div>
            Earned:&nbsp;
            <strong>{fmt(totalRealised)}</strong>
          </div>
        )}

        {unclaimedRewards > 0 && (
          <div>
            Pending:&nbsp;
            <strong>{fmt(unclaimedRewards)}</strong>
          </div>
        )}

        {unclaimedRewards > 0 && enableClaiming && (
          isClaiming ? (
            <Progress indeterminate style={{ marginTop: 10 }} />
          ) : (
            <Button
              rounded
              severity="warning"
              size="small"
              disabled={isClaiming}
              onClick={handleClaim}
              style={{ marginTop: 10, width: 200, justifyContent: 'center' }}
            >
              Claim
            </Button>
          )
        )}
      </div>
    );
  }
);

export default ULTRARewards;
